import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, CssBaseline } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { publicApi, PublicLandingPageData } from '../../services/publicApi';
import { landingPageApi } from '../../services/landingPageApi';
import {
  LandingPageHero,
  LandingPageAbout,
  LandingPageServices,
  LandingPageContact,
  LandingPageFooter,
  LeadMagnetWidget,
  LandingPageNotFound,
  LandingPageSkeleton,
} from '../../components/public';

interface PublicLandingPageProps {
  previewMode?: boolean;
}

export const PublicLandingPage = ({ previewMode = false }: PublicLandingPageProps) => {
  const { slug } = useParams<{ slug: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: previewMode ? ['landingPagePreview'] : ['publicLandingPage', slug],
    queryFn: async (): Promise<PublicLandingPageData> => {
      if (previewMode) {
        // Use authenticated preview API - works even when unpublished
        return landingPageApi.getPreview();
      }
      // Use public API - only works when published
      return publicApi.getLandingPage(slug!);
    },
    enabled: previewMode || !!slug,
    retry: false,
  });

  if (isLoading) {
    return <LandingPageSkeleton />;
  }

  if (error || !data) {
    return <LandingPageNotFound />;
  }

  const { owner, landingPage, leadMagnet, services, slug: dataSlug, name: businessName } = data;
  const primaryColor = landingPage.primaryColor || '#5D5FEF';
  // Use slug from data (for preview mode) or from URL params
  const effectiveSlug = dataSlug || slug;

  // Build meta title and description
  // Use business name if available, otherwise fall back to owner's name
  const ownerFullName = `${owner.firstName} ${owner.lastName}`;
  const metaTitle = businessName || ownerFullName;
  const metaDescription = landingPage.bio
    ? landingPage.bio.substring(0, 155) + (landingPage.bio.length > 155 ? '...' : '')
    : `${owner.firstName} ${owner.lastName} helps busy professionals reclaim their time.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta name="theme-color" content={primaryColor} />
      </Helmet>

      <CssBaseline />

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          '--primary-color': primaryColor,
        }}
      >
        {/* Hero Section */}
        <LandingPageHero
          owner={owner}
          headline={landingPage.headline}
          subheadline={landingPage.subheadline}
          primaryColor={primaryColor}
        />

        {/* About Section */}
        {landingPage.bio && (
          <LandingPageAbout bio={landingPage.bio} primaryColor={primaryColor} />
        )}

        {/* Services Section */}
        {landingPage.showServices && services && services.length > 0 && (
          <LandingPageServices services={services} primaryColor={primaryColor} />
        )}

        {/* Lead Magnet Section */}
        {leadMagnet?.enabled && effectiveSlug && (
          <LeadMagnetWidget
            slug={effectiveSlug}
            headline={leadMagnet.headline}
            subheadline={leadMagnet.subheadline}
            buttonText={leadMagnet.buttonText}
            primaryColor={primaryColor}
          />
        )}

        {/* Contact Section */}
        <LandingPageContact
          email={landingPage.contactEmail}
          phone={landingPage.phone}
          website={landingPage.website}
          linkedIn={landingPage.linkedIn}
          instagram={landingPage.instagram}
          primaryColor={primaryColor}
        />

        {/* Footer - grows to fill remaining space */}
        <Box sx={{ flexGrow: 1 }} />
        <LandingPageFooter primaryColor={primaryColor} />
      </Box>
    </>
  );
};

export default PublicLandingPage;
